import {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import FastModal from '../../components/FastModal';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import Labware, {WELL_HOVER_MODE} from '../../components/Labware';
import {Orientation} from '../../constants/dataEnum';
import {useStaticCallback} from '../../lib/customHooks';
import withIntl from '../../lib/withIntl';
import {showLoading, hideLoading, setIsAnyModalShow} from '../../stores/actions/appStateAction';
import {getHeadBrokenTips, setHeadBrokenTips} from '../../stores/business/headChannelsConfigBusiness';

const mapDispatchToProps = (dispatch) => ({
  onShowLoading: bindActionCreators(showLoading, dispatch),
  onHideLoading: bindActionCreators(hideLoading, dispatch),
  onGetHeadBrokenTips: bindActionCreators(getHeadBrokenTips, dispatch),
  onSetHeadBrokenTips: bindActionCreators(setHeadBrokenTips, dispatch),
  appStateActionSetIsAnyModalShow: bindActionCreators(setIsAnyModalShow, dispatch),
});

// eslint-disable-next-line no-unused-vars
function HeadChannelsConfig({
  intl,
  open,
  setOpen,
  onShowLoading,
  onHideLoading,
  onGetHeadBrokenTips,
  onSetHeadBrokenTips,
  appStateActionSetIsAnyModalShow,
}) {
  const [selectedWellStyleMap, setSelectedWellStyleMap] = useState({});

  const originalSelectedWellStyleMapRef = useRef({});

  const selectedWellStyle = {
    background: 'center/cover no-repeat url("/images/forbidden.png")',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    border: undefined,
    color: '#000000',
    opacity: 1,
  };

  const fetchHeadBrokenTips = async () => {
    const response = await onGetHeadBrokenTips();

    const newSelectedWellStyleMap = {};

    response.forEach((item) => {
      newSelectedWellStyleMap[`${parseInt(item.row, 10) + 1}-${parseInt(item.col, 10) + 1}`] = selectedWellStyle;
    });

    originalSelectedWellStyleMapRef.current = newSelectedWellStyleMap;
    setSelectedWellStyleMap(newSelectedWellStyleMap);
  };

  const handleConfirm = useStaticCallback(async () => {
    const wellPositions = Object.keys(selectedWellStyleMap).map((e) => ({
      Row: parseInt(e.split('-')[0], 10) - 1,
      Col: parseInt(e.split('-')[1], 10) - 1,
    }));

    onShowLoading();

    await onSetHeadBrokenTips(wellPositions);

    onHideLoading();
    appStateActionSetIsAnyModalShow(false);
    setOpen(false);
  });

  useEffect(() => {
    const onKeyEnter = (e) => {
      if (e.keyCode === 13) {
        const keys1 = Object.keys(selectedWellStyleMap).sort();
        const keys2 = Object.keys(originalSelectedWellStyleMapRef.current).sort();

        if (!(JSON.stringify(keys1) === JSON.stringify(keys2))) {
          handleConfirm();
        }
      }
    };

    document.addEventListener('keydown', onKeyEnter);

    return () => {
      document.removeEventListener('keydown', onKeyEnter);
    };
  }, [selectedWellStyleMap, handleConfirm]);

  useEffect(() => {
    if (!open) {
      return;
    }
    appStateActionSetIsAnyModalShow(true);
    fetchHeadBrokenTips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClickWell = useStaticCallback((wellID) => {
    if (selectedWellStyleMap[wellID]) {
      const newSelectedWellStyleMap = {...selectedWellStyleMap};
      delete newSelectedWellStyleMap[wellID];

      setSelectedWellStyleMap(newSelectedWellStyleMap);

      return;
    }

    setSelectedWellStyleMap({
      ...selectedWellStyleMap,
      [wellID]: selectedWellStyle,
    });
  });

  const isConfirmDisabled = () => {
    const keys1 = Object.keys(selectedWellStyleMap).sort();
    const keys2 = Object.keys(originalSelectedWellStyleMapRef.current).sort();

    return JSON.stringify(keys1) === JSON.stringify(keys2);
  };

  return (
    <FastModal
      className="head-channels-config"
      show={open}
      closeDisabled
      isValid
      btnAction={[
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: handleConfirm,
          disabled: isConfirmDisabled(),
          text: intl.formatMessage({id: 'headChannelsConfig.confirm'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            appStateActionSetIsAnyModalShow(false);
            setOpen(false);
          },
          text: intl.formatMessage({id: 'headChannelsConfig.cancel'}),
        },
      ]}
      header={intl.formatMessage({id: 'headChannelsConfig.header'})}
      content={
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <div style={{textAlign: 'center'}}>
            <FormattedHTMLMessage id="headChannelsConfig.message1" />
          </div>
          <div style={{height: '48vh', width: '32vh', padding: '1vh'}}>
            <Labware
              key="head-channels-config-labware"
              ID="head-channels-config-labware"
              colCount={12}
              rowCount={8}
              orientation={Orientation.POTRAIT}
              wellHoverMode={WELL_HOVER_MODE.NOTHING}
              isCustomTips
              isWellRectangle={false}
              wellStyle={selectedWellStyleMap}
              onClickWell={handleClickWell}
            />
          </div>
          <div style={{width: '30vh', textAlign: 'center', border: '1px solid lightgray'}}>
            {intl.formatMessage({id: 'headChannelsConfig.message2'})}
          </div>
        </div>
      }
    />
  );
}

HeadChannelsConfig.propTypes = {
  intl: PropTypes.object.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onGetHeadBrokenTips: PropTypes.func.isRequired,
  onSetHeadBrokenTips: PropTypes.func.isRequired,
  appStateActionSetIsAnyModalShow: PropTypes.func.isRequired,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};

HeadChannelsConfig.defaultProps = {
  open: false,
  setOpen: () => {},
};

export default connect(null, mapDispatchToProps)(withIntl(HeadChannelsConfig));
